/*
  sp-cart-subtotal.js
  Recalculates cart drawer / cart page subtotal when Samita Wholesale (or similar)
  updates line prices in the DOM. Shopify cart.total_price does not include those
  client-side tier discounts until checkout.
*/
(function () {
  const LINE_SELECTORS =
    '.horizontal-product, tr.cart-item, tr.sp-cart-sub-item, li.cart-drawer__sub-item';
  let debounceTimer = null;

  function parseMoneyToCents(text) {
    if (!text) return null;
    const cleaned = String(text).replace(/[^\d,.-]/g, '').trim();
    if (!cleaned) return null;

    let normalized;
    if (cleaned.includes(',') && cleaned.includes('.')) {
      normalized = cleaned.replace(/\./g, '').replace(',', '.');
    } else if (cleaned.includes(',')) {
      normalized = cleaned.replace(',', '.');
    } else {
      normalized = cleaned;
    }

    const amount = parseFloat(normalized);
    if (!Number.isFinite(amount)) return null;
    return Math.round(amount * 100);
  }

  function formatMoney(cents) {
    if (window.theme?.Currency?.formatMoney && window.theme?.settings?.moneyFormat) {
      return theme.Currency.formatMoney(cents, theme.settings.moneyFormat);
    }
    const amount = cents / 100;
    try {
      const locale = (document.documentElement.lang || 'nl-NL').replace('_', '-');
      const currency = window.Shopify?.currency?.active || 'EUR';
      return new Intl.NumberFormat(locale, { style: 'currency', currency }).format(amount);
    } catch (_) {
      return '€' + amount.toFixed(2).replace('.', ',');
    }
  }

  function parsePriceRootCents(priceRoot) {
    if (!priceRoot) return null;

    const samitaPrice = priceRoot.querySelector('.samitaWS-customized-price');
    if (samitaPrice) return parseMoneyToCents(samitaPrice.textContent);

    if (priceRoot.classList.contains('price--on-sale')) {
      const salePrice = priceRoot.querySelector('.price__regular');
      if (salePrice) return parseMoneyToCents(salePrice.textContent);
    }

    const regular = priceRoot.querySelector('.price__regular');
    if (regular) return parseMoneyToCents(regular.textContent);

    const clone = priceRoot.cloneNode(true);
    clone.querySelectorAll(
      '.sr-only, .samitaWS-customized-original-price, .price__sale, .price__tax, .unit-price'
    ).forEach((node) => node.remove());
    return parseMoneyToCents(clone.textContent);
  }

  function isSubItemLine(lineEl) {
    return lineEl.matches('tr.sp-cart-sub-item, li.cart-drawer__sub-item');
  }

  function getLineUnitPriceCents(lineEl) {
    const priceRoot = lineEl.querySelector('.price');
    if (!priceRoot) {
      const dataPrice = lineEl.getAttribute('data-price');
      return dataPrice ? parseInt(dataPrice, 10) : null;
    }

    const parsed = parsePriceRootCents(priceRoot);
    if (parsed != null) return parsed;

    const dataPrice = lineEl.getAttribute('data-price');
    return dataPrice ? parseInt(dataPrice, 10) : null;
  }

  function getSubItemLineTotalCents(lineEl) {
    const priceRoots = lineEl.querySelectorAll('.price');
    for (const priceRoot of priceRoots) {
      const parsed = parsePriceRootCents(priceRoot);
      if (parsed != null) return parsed;
    }
    return null;
  }

  function getLineQuantity(lineEl) {
    const input = lineEl.querySelector('input.quantity__input');
    if (input) {
      const qty = parseInt(input.value, 10);
      if (Number.isFinite(qty)) return qty;
    }
    const dataQty = lineEl.getAttribute('data-quantity');
    if (dataQty) return parseInt(dataQty, 10) || 0;
    return 0;
  }

  function getCartScope(configEl) {
    return (
      configEl.closest('[id^="MiniCart-"]') ||
      configEl.closest('[id^="MainCart-"]') ||
      configEl.closest('main-cart') ||
      configEl.closest('cart-items') ||
      document
    );
  }

  function updateSubtotalElement(subtotalEl) {
    const configEl = subtotalEl.closest('[data-sp-cart-totals]');
    if (!configEl) return;

    const threshold = parseInt(configEl.getAttribute('data-free-shipping-threshold') || '100000', 10);
    const shippingCost = parseInt(configEl.getAttribute('data-shipping-cost') || '399', 10);
    const scope = getCartScope(configEl);
    const lines = scope.querySelectorAll(LINE_SELECTORS);

    if (!lines.length) return;

    let itemsTotalCents = 0;
    lines.forEach((line) => {
      if (isSubItemLine(line)) {
        const lineTotalCents = getSubItemLineTotalCents(line);
        if (lineTotalCents == null) return;
        itemsTotalCents += lineTotalCents;
        return;
      }

      const unitCents = getLineUnitPriceCents(line);
      const qty = getLineQuantity(line);
      if (unitCents == null || qty <= 0) return;
      itemsTotalCents += unitCents * qty;
    });

    if (itemsTotalCents <= 0) return;

    const shippingCents = itemsTotalCents < threshold ? shippingCost : 0;
    const totalCents = itemsTotalCents + shippingCents;
    const formatted = formatMoney(totalCents);

    if (subtotalEl.textContent.trim() !== formatted) {
      subtotalEl.textContent = formatted;
    }
  }

  function updateAllSubtotals() {
    document.querySelectorAll('[data-cart-subtotal]').forEach(updateSubtotalElement);
  }

  function scheduleUpdate() {
    clearTimeout(debounceTimer);
    debounceTimer = setTimeout(updateAllSubtotals, 80);
  }

  function scheduleDelayedUpdates() {
    scheduleUpdate();
    window.setTimeout(updateAllSubtotals, 250);
    window.setTimeout(updateAllSubtotals, 800);
  }

  function observeCartItems(root) {
    if (!(root instanceof Element)) return;
    if (root.__spCartSubtotalObserved) return;
    root.__spCartSubtotalObserved = true;

    const observer = new MutationObserver(scheduleUpdate);
    observer.observe(root, { childList: true, subtree: true, characterData: true });
  }

  function bindObservers() {
    document.querySelectorAll('cart-items, [id^="MiniCart-"], [id^="MainCart-"]').forEach(observeCartItems);
  }

  document.addEventListener('cart:updated', scheduleDelayedUpdates);
  document.addEventListener('cart:refresh', scheduleDelayedUpdates);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      bindObservers();
      scheduleDelayedUpdates();
    });
  } else {
    bindObservers();
    scheduleDelayedUpdates();
  }

  document.addEventListener('cart:updated', bindObservers);
})();
